/**
 * The ping cap, as four settings rather than a block of JSON.
 *
 * It refuses a bad connection **at join**, never mid-match. That is a product
 * decision from 2026-09-17 and it is why `LatencyPolicy.Enforce` is compiled
 * false in the anticheat keys: an earlier build removed people from matches
 * they were already in, including on a freshly started server before any
 * override could reach it. This gate turns somebody away at the door instead,
 * which costs them a spawn fee they have not paid yet rather than a match they
 * are halfway through.
 *
 * EVERY FIELD IS OPTIONAL, and that is the whole shape of this editor. A field
 * left blank is not zero -- it is absent from the pushed value, and the game
 * keeps whatever its build compiled in. So each row has an explicit "use the
 * game's own" state, and clearing a box means "stop overriding this" rather
 * than "set it to nothing".
 *
 * The floors are the game's, not ours. It refuses a median under 150ms or a
 * jitter under 40ms, so the inputs refuse them too -- the server validates
 * again (`remote_config_keys.validate_join_latency_gate`), but finding out
 * after a round trip is a worse way to learn it.
 */
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { twMerge } from 'tailwind-merge'

export type JoinLatencyValue = {
    enabled?: boolean
    max_median_ms?: number
    max_jitter_ms?: number
    min_reply_fraction?: number
}

/** The floors the game itself enforces. Mirrored from
 *  `remote_config_keys.validate_join_latency_gate`. */
export const MEDIAN_FLOOR = 150
export const JITTER_FLOOR = 40

/** Whatever is stored, read as this form's shape.
 *
 *  Anything that is not an object reads as "no overrides at all", which is
 *  also what an empty key means, so a never-set key and a cleared one open the
 *  same way. */
export const asLatencyGate = (value: unknown): JoinLatencyValue => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    const raw = value as Record<string, unknown>
    const out: JoinLatencyValue = {}
    if (typeof raw.enabled === 'boolean') out.enabled = raw.enabled
    if (typeof raw.max_median_ms === 'number') out.max_median_ms = raw.max_median_ms
    if (typeof raw.max_jitter_ms === 'number') out.max_jitter_ms = raw.max_jitter_ms
    if (typeof raw.min_reply_fraction === 'number') out.min_reply_fraction = raw.min_reply_fraction
    return out
}

/** What is wrong with this, in words, or null.
 *
 *  Checked here as well as on the server because the server's refusal arrives
 *  after a save, and by then the operator has stopped thinking about the
 *  number they typed. */
export const latencyGateProblem = (value: JoinLatencyValue): string | null => {
    if (value.max_median_ms !== undefined && value.max_median_ms < MEDIAN_FLOOR)
        return `The game refuses a ping cap under ${MEDIAN_FLOOR}ms.`
    if (value.max_jitter_ms !== undefined && value.max_jitter_ms < JITTER_FLOOR)
        return `The game refuses a jitter cap under ${JITTER_FLOOR}ms.`
    if (
        value.min_reply_fraction !== undefined &&
        (value.min_reply_fraction < 0 || value.min_reply_fraction > 0.9)
    )
        return 'Replies must be between 0% and 90%.'
    return null
}

const Row = ({
    label,
    hint,
    children,
}: {
    label: string
    hint: string
    children: React.ReactNode
}) => (
    <label className='flex flex-row flex-wrap items-center gap-x-3 gap-y-1 border-b border-white/10 py-2 last:border-0'>
        <span className='flex w-48 shrink-0 flex-row items-center gap-1 text-[11px] uppercase tracking-widest text-white/60'>
            {label}
            <span title={hint} className='cursor-help'>
                <FontAwesomeIcon icon={faCircleInfo} className='opacity-40' />
            </span>
        </span>
        {children}
    </label>
)

const INPUT =
    'w-28 rounded border border-white/25 bg-transparent px-2 py-0.5 text-xs outline-none focus:border-white/50'

/** One optional number, with its "use the game's own" state spelled out. */
const Optional = ({
    value,
    onChange,
    placeholder,
    step,
    suffix,
}: {
    value: number | undefined
    onChange: (next: number | undefined) => void
    placeholder: string
    step?: string
    suffix: string
}) => (
    <>
        <input
            type='number'
            step={step}
            value={value ?? ''}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
            className={INPUT}
        />
        <span className='text-[11px] text-white/45'>
            {value === undefined ? "empty — the game keeps its own" : suffix}
        </span>
    </>
)

export const JoinLatencyGateForm = ({
    value,
    onChange,
}: {
    value: JoinLatencyValue
    onChange: (next: JoinLatencyValue) => void
}) => {
    const set = (patch: Partial<JoinLatencyValue>) => {
        const next = { ...value, ...patch }
        // An undefined field is dropped rather than sent as null: the key's
        // contract is that an absent field keeps the build's compiled value,
        // and null is not absent.
        for (const key of Object.keys(next) as Array<keyof JoinLatencyValue>) {
            if (next[key] === undefined) delete next[key]
        }
        onChange(next)
    }
    const problem = latencyGateProblem(value)
    const off = value.enabled === false

    return (
        <div className='flex flex-col'>
            <p className='pb-2 text-white/55'>
                Turns a player away at the door when their connection is too poor to play fairly.
                Nobody is ever removed from a match they are already in.
            </p>

            <Row
                label='Gate'
                hint='Off means the game stops checking connections at join entirely, whatever the caps below say.'
            >
                <select
                    value={value.enabled === undefined ? '' : String(value.enabled)}
                    onChange={(e) =>
                        set({ enabled: e.target.value === '' ? undefined : e.target.value === 'true' })
                    }
                    className='rounded border border-white/25 bg-transparent px-2 py-0.5 text-xs text-white outline-none focus:border-white/50'
                >
                    <option value='' className='bg-neutral-900'>
                        use the game&rsquo;s own
                    </option>
                    <option value='true' className='bg-neutral-900'>
                        on
                    </option>
                    <option value='false' className='bg-neutral-900'>
                        off
                    </option>
                </select>
            </Row>

            <div className={twMerge('flex flex-col', off && 'opacity-40')}>
                <Row
                    label='Ping cap'
                    hint={`Median round trip. Above this the player is refused at join. The game will not accept anything under ${MEDIAN_FLOOR}ms.`}
                >
                    <Optional
                        value={value.max_median_ms}
                        onChange={(max_median_ms) => set({ max_median_ms })}
                        placeholder={String(MEDIAN_FLOOR)}
                        suffix='ms or worse is refused'
                    />
                </Row>

                <Row
                    label='Jitter cap'
                    hint={`How much the ping is allowed to swing. A steady 200ms plays better than one jumping between 60 and 300. Minimum ${JITTER_FLOOR}ms.`}
                >
                    <Optional
                        value={value.max_jitter_ms}
                        onChange={(max_jitter_ms) => set({ max_jitter_ms })}
                        placeholder={String(JITTER_FLOOR)}
                        suffix='ms of swing or worse is refused'
                    />
                </Row>

                <Row
                    label='Replies needed'
                    hint='How many of the test pings have to come back at all. A connection that drops half of them is refused however fast the rest were. 0 to 0.9.'
                >
                    <Optional
                        value={value.min_reply_fraction}
                        onChange={(min_reply_fraction) => set({ min_reply_fraction })}
                        placeholder='0.5'
                        step='0.05'
                        suffix={`${Math.round((value.min_reply_fraction ?? 0) * 100)}% must come back`}
                    />
                </Row>
            </div>

            {off && (
                <p className='pt-2 text-amber-300'>
                    The gate is off, so the caps above are not applied.
                </p>
            )}
            {problem && <p className='pt-2 text-red-400'>{problem}</p>}
        </div>
    )
}
